import { useInfiniteQuery } from "@tanstack/react-query";
import { useEffect, useMemo } from "react";
import { toast } from "sonner";
import { PropertyFilters } from "@/components/find/PropertyFilters";
import { MockProperty } from "@/data/mockProperties";
import { supabase } from "@/integrations/supabase/client";

export const PROPERTY_SEARCH_PAGE_SIZE = 40;

/**
 * MLS results don't move minute-to-minute, so a remount inside this window is a
 * cache hit rather than a refetch. Users force fresh data with `refresh()`.
 */
const SEARCH_STALE_TIME = 10 * 60 * 1000;
const SEARCH_GC_TIME = 30 * 60 * 1000;

const EMPTY_PAGE: PropertySearchResult = {
  properties: [],
  total: 0,
  hasMore: false,
};

interface PropertySearchResult {
  properties: MockProperty[];
  total: number;
  hasMore: boolean;
}

interface ParsedLocation {
  city?: string;
  state?: string;
  zip?: string;
}

/** Splits the free-text search box into the city/state/zip the edge function expects. */
export function parseLocation(searchQuery: string): ParsedLocation {
  const query = searchQuery.trim();

  // 5 digits — a zip code
  if (/^\d{5}$/.test(query)) {
    return { zip: query };
  }
  // "City, ST" format
  if (query.includes(",")) {
    const parts = query.split(",").map((part) => part.trim());
    return { city: parts[0], state: parts[1]?.toUpperCase() };
  }
  // Just a state abbreviation
  if (query.length === 2) {
    return { state: query.toUpperCase() };
  }
  // Assume it's a city
  if (query) {
    return { city: query };
  }
  return {};
}

async function fetchPage(
  filters: PropertyFilters,
  offset: number,
): Promise<PropertySearchResult> {
  const { city, state, zip } = parseLocation(filters.search);

  const requestBody = {
    city,
    state,
    zip,
    minPrice: filters.priceMin,
    maxPrice: filters.priceMax,
    minBeds: filters.bedsMin,
    minBaths: filters.bathsMin,
    sqftMin: filters.sqftMin,
    sqftMax: filters.sqftMax,
    propertyTypes:
      filters.propertyTypes.length > 0 ? filters.propertyTypes : undefined,
    foreclosure: filters.foreclosure || undefined,
    preForeclosure: filters.preForeclosure || undefined,
    auction: filters.auction || undefined,
    reo: filters.reo || undefined,
    capRateMin: filters.capRateMin,
    cashOnCashMin: filters.cashOnCashMin,
    grmMax: filters.grmMax,
    priceReduced: filters.priceReduced || undefined,
    belowMarketValue: filters.belowMarketValue || undefined,
    motivatedSeller: filters.motivatedSeller || undefined,
    daysOnMarketMax: filters.daysOnMarketMax,
    limit: PROPERTY_SEARCH_PAGE_SIZE,
    offset,
  };

  const { data, error } = await supabase.functions.invoke<PropertySearchResult>(
    "property-search",
    { body: requestBody },
  );

  if (error) {
    throw error;
  }

  return {
    properties: data?.properties ?? [],
    total: data?.total ?? 0,
    hasMore: data?.hasMore ?? false,
  };
}

/**
 * Live MLS property search.
 *
 * Pass the *committed* filters — the ones the user actually searched for, not the
 * current state of the filter inputs. `null` means nothing has been searched yet
 * and the query stays disabled, so opening the page costs zero requests.
 *
 * Because the committed filters are the query key, pagination can only ever page
 * the search that produced page 1, and revisiting a previous search is served
 * from cache instead of refetched.
 */
export function usePropertySearch(filters: PropertyFilters | null) {
  const query = useInfiniteQuery({
    queryKey: ["property-search", filters],
    queryFn: ({ pageParam }) =>
      filters ? fetchPage(filters, pageParam) : Promise.resolve(EMPTY_PAGE),
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) =>
      lastPage.hasMore
        ? allPages.reduce((count, page) => count + page.properties.length, 0)
        : undefined,
    enabled: filters !== null,
    staleTime: SEARCH_STALE_TIME,
    gcTime: SEARCH_GC_TIME,
  });

  const properties = useMemo(
    () => query.data?.pages.flatMap((page) => page.properties) ?? [],
    [query.data],
  );

  const error = query.error;
  const errorMessage = useMemo(() => {
    if (!error) return null;
    return error instanceof Error ? error.message : "Failed to search properties";
  }, [error]);

  useEffect(() => {
    if (errorMessage) {
      toast.error("Property search failed", { description: errorMessage });
    }
  }, [errorMessage]);

  return {
    properties,
    total: query.data?.pages[0]?.total ?? 0,
    hasMore: query.hasNextPage,
    isLoading: query.isLoading,
    isLoadingMore: query.isFetchingNextPage,
    isRefreshing: query.isRefetching && !query.isFetchingNextPage,
    error: errorMessage,
    loadMore: query.fetchNextPage,
    refresh: query.refetch,
  };
}
